import { type MutationStatus, useMutationState } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { constants } from '@/constants';
import { APPOINTMENT_RESCHEDULE_MUTATION_KEY } from '../api/patchConfirmation';
import { APPOINTMENT_CREATION_MUTATION_KEY } from '../api/useAppointmentCreation';

type MutationSource = 'create' | 'reschedule';

type TrackedMutationState = {
  status: MutationStatus;
  error: unknown;
  submittedAt: number;
};

type MutationResult = {
  source: MutationSource;
  mutation: TrackedMutationState;
};

const selectMutationState = (mutation: {
  state: TrackedMutationState;
}): TrackedMutationState => ({
  status: mutation.state.status,
  error: mutation.state.error,
  submittedAt: mutation.state.submittedAt,
});

const getLatestMutation = (states: TrackedMutationState[]) =>
  states.reduce<TrackedMutationState | undefined>((latest, current) => {
    if (!latest) return current;

    return current.submittedAt > latest.submittedAt ? current : latest;
  }, undefined);

const isAbortedMutationError = (error: unknown) =>
  error instanceof Error && error.message === constants.TANSTACK_ABORTED_ERROR;

const getLatestErrorMutation = (
  latestCreation: TrackedMutationState | undefined,
  latestReschedule: TrackedMutationState | undefined,
) => {
  const candidates: MutationResult[] = [
    latestCreation && { source: 'create' as const, mutation: latestCreation },
    latestReschedule && {
      source: 'reschedule' as const,
      mutation: latestReschedule,
    },
  ].filter(Boolean) as MutationResult[];

  return candidates.reduce<MutationResult | null>((latest, current) => {
    if (
      current.mutation.status !== 'error' ||
      isAbortedMutationError(current.mutation.error)
    ) {
      return latest;
    }
    if (!latest || current.mutation.submittedAt > latest.mutation.submittedAt) {
      return current;
    }
    return latest;
  }, null);
};

const useAppointmentMutationStatus = () => {
  const creationMutations = useMutationState({
    filters: { mutationKey: APPOINTMENT_CREATION_MUTATION_KEY },
    select: selectMutationState,
  });

  const rescheduleMutations = useMutationState({
    filters: { mutationKey: APPOINTMENT_RESCHEDULE_MUTATION_KEY },
    select: selectMutationState,
  });

  const latestCreation = getLatestMutation(creationMutations);
  const latestReschedule = getLatestMutation(rescheduleMutations);

  const latestErrorMutation = getLatestErrorMutation(
    latestCreation,
    latestReschedule,
  );

  const [persistedErrorSource, setPersistedErrorSource] =
    useState<MutationSource | null>(null);

  const hasTrackedMutations =
    creationMutations.length > 0 || rescheduleMutations.length > 0;

  useEffect(() => {
    if (!hasTrackedMutations) {
      return;
    }

    setPersistedErrorSource(latestErrorMutation?.source ?? null);
  }, [hasTrackedMutations, latestErrorMutation]);

  const isCreationMutationPending = creationMutations.some(
    (mutation) => mutation.status === 'pending',
  );

  const isRescheduleMutationPending = rescheduleMutations.some(
    (mutation) => mutation.status === 'pending',
  );

  const isLoading = isCreationMutationPending || isRescheduleMutationPending;
  const errorSource = latestErrorMutation?.source ?? persistedErrorSource;

  return {
    isLoading,
    isError: errorSource !== null,
    errorSource,
  };
};

export default useAppointmentMutationStatus;
